// frontend/src/components/crm/CampaignsEnhanced.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { campaignsAPI, audiencesAPI } from '../../utils/crmApi';
import {
    PaperAirplaneIcon,
    PlusIcon,
    PlayIcon,
    PauseIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    ChartBarIcon,
    UsersIcon,
    ClockIcon,
    CheckCircleIcon,
    XCircleIcon,
    MegaphoneIcon,
    ArrowPathIcon,
    SparklesIcon,
    FunnelIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useDialog, ConfirmationDialog, Dialog, DialogHeader, DialogBody, DialogFooter } from '../../components/kibo-ui/dialog';
import {
    TableProvider,
    Table,
    TableHeader,
    TableSearch,
    TableHead,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableFooter,
    StatusBadge
} from '../../components/kibo-ui/table';

const CampaignsEnhanced = () => {
    const navigate = useNavigate();
    const { openDialog } = useDialog();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Get initial tab from URL query parameter, default to 'campaigns'
    const initialTab = searchParams.get('tab') === 'audiences' ? 'audiences' : 'campaigns';
    const [activeTab, setActiveTab] = useState(initialTab); // 'campaigns' or 'audiences'
    
    // Update URL when tab changes (but don't add query param if it's the default 'campaigns')
    useEffect(() => {
        const currentTabParam = searchParams.get('tab');
        if (activeTab === 'audiences' && currentTabParam !== 'audiences') {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('tab', 'audiences');
            setSearchParams(newSearchParams, { replace: true });
        } else if (activeTab === 'campaigns' && currentTabParam === 'audiences') {
            // Remove the tab param if switching back to campaigns
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('tab');
            setSearchParams(newSearchParams, { replace: true });
        }
    }, [activeTab, searchParams, setSearchParams]);
    
    // Campaigns state
    const [campaigns, setCampaigns] = useState([]);
    const [campaignsLoading, setCampaignsLoading] = useState(true);
    const [campaignsSearch, setCampaignsSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [campaignsPagination, setCampaignsPagination] = useState({ page: 1, limit: 20, total: 0 });
    const [selectedCampaigns, setSelectedCampaigns] = useState([]);
    
    // Audiences state
    const [audiences, setAudiences] = useState([]);
    const [audiencesLoading, setAudiencesLoading] = useState(true);
    const [audiencesSearch, setAudiencesSearch] = useState('');
    const [audienceTypeFilter, setAudienceTypeFilter] = useState('');
    const [audiencesPagination, setAudiencesPagination] = useState({ page: 1, limit: 20, total: 0 });
    const [viewingAudienceContacts, setViewingAudienceContacts] = useState(null);
    const [audienceContacts, setAudienceContacts] = useState([]);
    const [audienceContactsLoading, setAudienceContactsLoading] = useState(false);

    // Campaign status colors
    const getStatusColor = (status) => {
        const colors = {
            'draft': 'bg-gray-100 text-gray-700',
            'scheduled': 'bg-blue-100 text-blue-700',
            'running': 'bg-yellow-100 text-yellow-700',
            'paused': 'bg-orange-100 text-orange-700',
            'completed': 'bg-green-100 text-green-700',
            'cancelled': 'bg-red-100 text-red-700'
        };
        return colors[status] || 'bg-gray-100 text-gray-700';
    };

    // Campaign type icons and labels
    const getTypeInfo = (type) => {
        const types = {
            'broadcast': { icon: '📢', label: 'Broadcast', color: 'text-blue-600' },
            'sequence': { icon: '🔄', label: 'Sequence', color: 'text-purple-600' },
            'journey': { icon: '🎯', label: 'Journey', color: 'text-green-600' }
        };
        return types[type] || { icon: '📧', label: type, color: 'text-gray-600' };
    };

    const loadCampaigns = useCallback(async () => {
        try {
            setCampaignsLoading(true);
            const params = {
                page: campaignsPagination.page,
                limit: campaignsPagination.limit,
                ...(campaignsSearch && { search: campaignsSearch }),
                ...(statusFilter && { status: statusFilter }),
                ...(typeFilter && { type: typeFilter })
            };

            console.log('Loading campaigns with params:', params);
            const response = await campaignsAPI.getAll(params);
            console.log('Campaigns API response:', response);
            
            if (response && response.data) {
                if (response.data.success) {
                    setCampaigns(response.data.data || []);
                    if (response.data.pagination) {
                        setCampaignsPagination(prev => ({
                            ...prev,
                            ...response.data.pagination
                        }));
                    }
                } else {
                    console.error('Campaigns API returned success:false:', response.data);
                    toast.error(response.data.message || 'Failed to load campaigns');
                    setCampaigns([]);
                }
            } else {
                console.error('Invalid response structure:', response);
                toast.error('Invalid response from server');
                setCampaigns([]);
            }
        } catch (error) {
            console.error('Failed to load campaigns:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            toast.error(error.response?.data?.message || error.message || 'Failed to load campaigns');
            setCampaigns([]);
        } finally {
            setCampaignsLoading(false);
        }
    }, [campaignsSearch, statusFilter, typeFilter, campaignsPagination.page]);

    const loadAudiences = useCallback(async () => {
        try {
            setAudiencesLoading(true);
            const params = {
                page: audiencesPagination.page,
                limit: audiencesPagination.limit,
                ...(audiencesSearch && { search: audiencesSearch }),
                ...(audienceTypeFilter && { type: audienceTypeFilter })
            };

            const response = await audiencesAPI.getAll(params);
            if (response.data.success) {
                setAudiences(response.data.data);
                if (response.data.pagination) {
                    setAudiencesPagination(prev => ({
                        ...prev,
                        ...response.data.pagination
                    }));
                }
            }
        } catch (error) {
            console.error('Failed to load audiences:', error);
            toast.error('Failed to load audiences');
        } finally {
            setAudiencesLoading(false);
        }
    }, [audiencesSearch, audienceTypeFilter, audiencesPagination.page]);

    useEffect(() => {
        if (activeTab === 'campaigns') {
            loadCampaigns();
        } else {
            loadAudiences();
        }
    }, [activeTab, loadCampaigns, loadAudiences]);

    const handleSelectCampaign = (campaignId) => {
        setSelectedCampaigns(prev =>
            prev.includes(campaignId)
                ? prev.filter(id => id !== campaignId)
                : [...prev, campaignId]
        );
    };

    const handleSelectAll = () => {
        if (selectedCampaigns.length === campaigns.length) {
            setSelectedCampaigns([]);
        } else {
            setSelectedCampaigns(campaigns.map(c => c._id));
        }
    };

    const handleSchedule = async (campaignId) => {
        openDialog({
            title: 'Schedule Campaign',
            message: 'Enter the date and time to schedule this campaign:',
            type: 'confirm',
            onConfirm: async () => {
                try {
                    const scheduledAt = new Date(); // In real implementation, get from date picker
                    await campaignsAPI.schedule(campaignId, { scheduledAt });
                    toast.success('Campaign scheduled successfully');
                    loadCampaigns();
                } catch (error) {
                    toast.error('Failed to schedule campaign');
                }
            }
        });
    };

    const handlePause = async (campaignId) => {
        try {
            await campaignsAPI.pause(campaignId);
            toast.success('Campaign paused');
            loadCampaigns();
        } catch (error) {
            toast.error('Failed to pause campaign');
        }
    };

    const handleResume = async (campaignId) => {
        try {
            await campaignsAPI.resume(campaignId);
            toast.success('Campaign resumed');
            loadCampaigns();
        } catch (error) {
            toast.error('Failed to resume campaign');
        }
    };

    const handleDuplicate = async (campaignId) => {
        try {
            const response = await campaignsAPI.duplicate(campaignId);
            if (response.data.success) {
                toast.success('Campaign duplicated successfully');
                loadCampaigns();
                // Navigate to the duplicated campaign
                if (response.data.data._id) {
                    navigate(`/campaigns/${response.data.data._id}`);
                }
            }
        } catch (error) {
            console.error('Failed to duplicate campaign:', error);
            toast.error(error.response?.data?.message || 'Failed to duplicate campaign');
        }
    };

    const handleDelete = (campaignId) => {
        openDialog({
            title: 'Delete Campaign',
            message: 'Are you sure you want to delete this campaign? This action cannot be undone.',
            type: 'confirm',
            onConfirm: async () => {
                try {
                    await campaignsAPI.delete(campaignId);
                    toast.success('Campaign deleted');
                    loadCampaigns();
                } catch (error) {
                    toast.error('Failed to delete campaign');
                }
            }
        });
    };

    const formatNumber = (num) => {
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return num?.toString() || '0';
    };

    const getKPIDisplay = (campaign) => {
        const analytics = campaign.analytics || {};
        return {
            sent: formatNumber(analytics.sent || 0),
            openRate: analytics.delivered > 0 
                ? `${((analytics.uniqueOpened || 0) / analytics.delivered * 100).toFixed(1)}%`
                : '0%',
            clickRate: analytics.delivered > 0
                ? `${((analytics.uniqueClicked || 0) / analytics.delivered * 100).toFixed(1)}%`
                : '0%'
        };
    };

    // Audiences handlers
    const handleRecalculateAudience = async (audienceId) => {
        try {
            await audiencesAPI.calculateSize(audienceId);
            toast.success('Audience size recalculated');
            loadAudiences();
        } catch (error) {
            toast.error('Failed to recalculate audience size');
        }
    };

    const handleDeleteAudience = (audienceId) => {
        openDialog({
            title: 'Delete Audience',
            message: 'Are you sure you want to delete this audience? This action cannot be undone.',
            type: 'confirm',
            onConfirm: async () => {
                try {
                    await audiencesAPI.delete(audienceId);
                    toast.success('Audience deleted');
                    loadAudiences();
                } catch (error) {
                    toast.error('Failed to delete audience');
                }
            }
        });
    };

    // Load audience contacts when viewing
    useEffect(() => {
        if (viewingAudienceContacts) {
            loadAudienceContacts(viewingAudienceContacts._id);
        }
    }, [viewingAudienceContacts]);

    const loadAudienceContacts = async (audienceId) => {
        try {
            setAudienceContactsLoading(true);
            const response = await audiencesAPI.getById(audienceId);
            if (response.data.success) {
                const audience = response.data.data;
                // Extract contacts from audience.contacts array
                // Handle both old format (array of IDs) and new format (array of objects)
                const contacts = (audience.contacts || []).map(contact => {
                    if (typeof contact === 'object' && contact.email) {
                        // New format: already has email, name, contactType
                        return {
                            email: contact.email,
                            name: contact.name || contact.email,
                            contactType: contact.contactType || 'client',
                            contactId: contact.contactId
                        };
                    }
                    // Old format: just an ID, we'd need to fetch details
                    // For now, return what we can
                    return {
                        email: 'Loading...',
                        name: 'Loading...',
                        contactType: 'client',
                        contactId: typeof contact === 'string' ? contact : contact._id
                    };
                });
                setAudienceContacts(contacts);
            }
        } catch (error) {
            console.error('Failed to load audience contacts:', error);
            toast.error('Failed to load audience contacts');
        } finally {
            setAudienceContactsLoading(false);
        }
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Campaigns & Audiences</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Create and manage email campaigns, sequences, journeys, and audiences
                    </p>
                </div>
                {activeTab === 'campaigns' ? (
                    <Link
                        to="/campaigns/new"
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
                    >
                        <PlusIcon className="h-5 w-5" />
                        Create Campaign
                    </Link>
                ) : (
                    <Link
                        to="/audiences/new"
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
                    >
                        <PlusIcon className="h-5 w-5" />
                        Create Audience
                    </Link>
                )}
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b border-border">
                <button
                    onClick={() => setActiveTab('campaigns')}
                    className={`px-4 py-2 font-medium text-sm transition-colors ${
                        activeTab === 'campaigns'
                            ? 'text-primary border-b-2 border-primary'
                            : 'text-muted-foreground hover:text-foreground'
                    }`}
                >
                    Campaigns
                </button>
                <button
                    onClick={() => setActiveTab('audiences')}
                    className={`px-4 py-2 font-medium text-sm transition-colors ${
                        activeTab === 'audiences'
                            ? 'text-primary border-b-2 border-primary'
                            : 'text-muted-foreground hover:text-foreground'
                    }`}
                >
                    Audiences
                </button>
            </div>

            {activeTab === 'campaigns' ? (
                <>
                    {/* Campaigns Filters */}
                    <div className="bg-card border border-border rounded-lg p-4 mb-6">
                        <div className="flex flex-wrap gap-4">
                            <div className="flex-1 min-w-[200px]">
                                <input
                                    type="text"
                                    placeholder="Search campaigns..."
                                    value={campaignsSearch}
                                    onChange={(e) => setCampaignsSearch(e.target.value)}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                />
                            </div>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
                            >
                                <option value="">All Statuses</option>
                                <option value="draft">Draft</option>
                                <option value="scheduled">Scheduled</option>
                                <option value="running">Running</option>
                                <option value="paused">Paused</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
                            >
                                <option value="">All Types</option>
                                <option value="broadcast">Broadcast</option>
                                <option value="sequence">Sequence</option>
                                <option value="journey">Journey</option>
                            </select>
                        </div>
                    </div>

                    {/* Campaigns Table */}
                    <div className="bg-card border border-border rounded-lg overflow-hidden">
                        <TableProvider data={[]} columns={[]}>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-border">
                                    <TableHead>
                                        <tr>
                                            <TableHeaderCell className="w-12">
                                            <input
                                                type="checkbox"
                                                checked={selectedCampaigns.length === campaigns.length && campaigns.length > 0}
                                                onChange={handleSelectAll}
                                                className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                            />
                                        </TableHeaderCell>
                                        <TableHeaderCell>Campaign</TableHeaderCell>
                                        <TableHeaderCell>Type</TableHeaderCell>
                                        <TableHeaderCell>Status</TableHeaderCell>
                                        <TableHeaderCell>Audience</TableHeaderCell>
                                        <TableHeaderCell>Performance</TableHeaderCell>
                                        <TableHeaderCell>Next Send</TableHeaderCell>
                                        <TableHeaderCell>Actions</TableHeaderCell>
                                    </tr>
                                </TableHead>
                                <TableBody>
                                    {campaignsLoading ? (
                                        <tr>
                                            <td colSpan={8} className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-foreground text-center py-8 text-muted-foreground">
                                                Loading campaigns...
                                            </td>
                                        </tr>
                                    ) : campaigns.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-foreground text-center py-8 text-muted-foreground">
                                                No campaigns found. Create your first campaign to get started.
                                            </td>
                                        </tr>
                                    ) : (
                                        campaigns.map((campaign) => {
                                            const typeInfo = getTypeInfo(campaign.type);
                                            const kpis = getKPIDisplay(campaign);
                                            
                                            return (
                                                <TableRow
                                                    key={campaign._id}
                                                    onClick={() => navigate(`/campaigns/${campaign._id}`)}
                                                    className="cursor-pointer"
                                                >
                                                    <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedCampaigns.includes(campaign._id)}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                handleSelectCampaign(campaign._id);
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="h-4 w-4 text-primary focus:ring-primary border-input rounded"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <div>
                                                            <div className="font-medium text-foreground">{campaign.name}</div>
                                                            {campaign.description && (
                                                                <div className="text-sm text-muted-foreground mt-1">
                                                                    {campaign.description.substring(0, 60)}
                                                                    {campaign.description.length > 60 ? '...' : ''}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-lg">{typeInfo.icon}</span>
                                                            <span className={typeInfo.color}>{typeInfo.label}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <StatusBadge status={campaign.status} className={getStatusColor(campaign.status)} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2 text-sm">
                                                            <UsersIcon className="h-4 w-4 text-muted-foreground" />
                                                            {campaign.audienceId?.estimatedSize 
                                                                ? formatNumber(campaign.audienceId.estimatedSize)
                                                                : 'N/A'}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        {campaign.analytics?.sent > 0 ? (
                                                            <div className="text-sm space-y-1">
                                                                <div>Sent: {kpis.sent}</div>
                                                                <div className="text-muted-foreground">
                                                                    Opens: {kpis.openRate} | Clicks: {kpis.clickRate}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-sm text-muted-foreground">Not sent yet</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {campaign.scheduledAt ? (
                                                            <div className="text-sm">
                                                                <div>{new Date(campaign.scheduledAt).toLocaleDateString()}</div>
                                                                <div className="text-muted-foreground">
                                                                    {new Date(campaign.scheduledAt).toLocaleTimeString()}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-sm text-muted-foreground">—</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                                        <div className="flex items-center gap-2">
                                                            {campaign.status === 'running' && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handlePause(campaign._id);
                                                                    }}
                                                                    className="text-orange-600 hover:text-orange-800"
                                                                    title="Pause"
                                                                >
                                                                    <PauseIcon className="h-4 w-4" />
                                                                </button>
                                                            )}
                                                            {campaign.status === 'paused' && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleResume(campaign._id);
                                                                    }}
                                                                    className="text-green-600 hover:text-green-800"
                                                                    title="Resume"
                                                                >
                                                                    <PlayIcon className="h-4 w-4" />
                                                                </button>
                                                            )}
                                                            {campaign.status === 'draft' && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleSchedule(campaign._id);
                                                                    }}
                                                                    className="text-blue-600 hover:text-blue-800"
                                                                    title="Schedule"
                                                                >
                                                                    <ClockIcon className="h-4 w-4" />
                                                                </button>
                                                            )}
                                                            <Link
                                                                to={`/campaigns/${campaign._id}/analytics`}
                                                                className="text-blue-600 hover:text-blue-800"
                                                                title="Analytics"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <ChartBarIcon className="h-4 w-4" />
                                                            </Link>
                                                            <Link
                                                                to={`/campaigns/${campaign._id}/edit`}
                                                                className="text-muted-foreground hover:text-foreground"
                                                                title="Edit"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <PencilIcon className="h-4 w-4" />
                                                            </Link>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDuplicate(campaign._id);
                                                                }}
                                                                className="text-blue-600 hover:text-blue-800"
                                                                title="Duplicate"
                                                            >
                                                                <ArrowPathIcon className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(campaign._id);
                                                                }}
                                                                className="text-red-600 hover:text-red-800"
                                                                title="Delete"
                                                            >
                                                                <TrashIcon className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {campaignsPagination.pages > 1 && (
                            <div className="px-4 py-3 border-t border-border flex items-center justify-between">
                                <div className="text-sm text-muted-foreground">
                                    Showing {((campaignsPagination.page - 1) * campaignsPagination.limit) + 1} to{' '}
                                    {Math.min(campaignsPagination.page * campaignsPagination.limit, campaignsPagination.total)} of {campaignsPagination.total} campaigns
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCampaignsPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={campaignsPagination.page === 1}
                                        className="px-3 py-1 border border-input rounded-md disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setCampaignsPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={campaignsPagination.page >= campaignsPagination.pages}
                                        className="px-3 py-1 border border-input rounded-md disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                        </TableProvider>
                    </div>
                </>
            ) : (
                <>
                    {/* Audiences Filters */}
                    <div className="bg-card border border-border rounded-lg p-4 mb-6">
                        <div className="flex flex-wrap gap-4">
                            <div className="flex-1 min-w-[200px]">
                                <input
                                    type="text"
                                    placeholder="Search audiences..."
                                    value={audiencesSearch}
                                    onChange={(e) => setAudiencesSearch(e.target.value)}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                />
                            </div>
                            <select
                                value={audienceTypeFilter}
                                onChange={(e) => setAudienceTypeFilter(e.target.value)}
                                className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
                            >
                                <option value="">All Types</option>
                                <option value="static">Static List</option>
                                <option value="segment">Dynamic Segment</option>
                            </select>
                        </div>
                    </div>

                    {/* Audiences Table */}
                    <div className="bg-card border border-border rounded-lg overflow-hidden">
                        <TableProvider data={[]} columns={[]}>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-border">
                                    <TableHead>
                                        <tr>
                                            <TableHeaderCell>Name</TableHeaderCell>
                                        <TableHeaderCell>Type</TableHeaderCell>
                                        <TableHeaderCell>Size</TableHeaderCell>
                                        <TableHeaderCell>Last Calculated</TableHeaderCell>
                                        <TableHeaderCell>Actions</TableHeaderCell>
                                    </tr>
                                </TableHead>
                                <TableBody>
                                    {audiencesLoading ? (
                                        <tr>
                                            <td colSpan={5} className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-foreground text-center py-8 text-muted-foreground">
                                                Loading audiences...
                                            </td>
                                        </tr>
                                    ) : audiences.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-foreground text-center py-8 text-muted-foreground">
                                                No audiences found. Create your first audience to get started.
                                            </td>
                                        </tr>
                                    ) : (
                                        audiences.map((audience) => (
                                            <TableRow
                                                key={audience._id}
                                                onClick={() => navigate(`/audiences/${audience._id}`)}
                                                className="cursor-pointer"
                                            >
                                                <TableCell>
                                                    <div>
                                                        <div className="font-medium text-foreground">{audience.name}</div>
                                                        {audience.description && (
                                                            <div className="text-sm text-muted-foreground mt-1">
                                                                {audience.description.substring(0, 60)}
                                                                {audience.description.length > 60 ? '...' : ''}
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <StatusBadge className={
                                                        audience.type === 'static'
                                                            ? 'bg-blue-100 text-blue-700'
                                                            : 'bg-purple-100 text-purple-700'
                                                    }>
                                                        {audience.type === 'static' ? (
                                                            <span className="flex items-center gap-1">
                                                                <UsersIcon className="h-3 w-3" />
                                                                Static List
                                                            </span>
                                                        ) : (
                                                            <span className="flex items-center gap-1">
                                                                <FunnelIcon className="h-3 w-3" />
                                                                Segment
                                                            </span>
                                                        )}
                                                    </StatusBadge>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <UsersIcon className="h-4 w-4 text-muted-foreground" />
                                                        {formatNumber(audience.estimatedSize || 0)}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {audience.lastCalculatedAt ? (
                                                        <span className="text-sm text-muted-foreground">
                                                            {new Date(audience.lastCalculatedAt).toLocaleDateString()}
                                                        </span>
                                                    ) : (
                                                        <span className="text-sm text-muted-foreground">Never</span>
                                                    )}
                                                </TableCell>
                                                <TableCell onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setViewingAudienceContacts(audience);
                                                            }}
                                                            className="text-green-600 hover:text-green-800"
                                                            title="View Contacts"
                                                        >
                                                            <EyeIcon className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRecalculateAudience(audience._id);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800"
                                                            title="Recalculate Size"
                                                        >
                                                            <ArrowPathIcon className="h-4 w-4" />
                                                        </button>
                                                        <Link
                                                            to={`/audiences/${audience._id}/edit`}
                                                            className="text-muted-foreground hover:text-foreground"
                                                            title="Edit"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <PencilIcon className="h-4 w-4" />
                                                        </Link>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteAudience(audience._id);
                                                            }}
                                                            className="text-red-600 hover:text-red-800"
                                                            title="Delete"
                                                        >
                                                            <TrashIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {audiencesPagination.pages > 1 && (
                            <div className="px-4 py-3 border-t border-border flex items-center justify-between">
                                <div className="text-sm text-muted-foreground">
                                    Showing {((audiencesPagination.page - 1) * audiencesPagination.limit) + 1} to{' '}
                                    {Math.min(audiencesPagination.page * audiencesPagination.limit, audiencesPagination.total)} of {audiencesPagination.total} audiences
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setAudiencesPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={audiencesPagination.page === 1}
                                        className="px-3 py-1 border border-input rounded-md disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setAudiencesPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={audiencesPagination.page >= audiencesPagination.pages}
                                        className="px-3 py-1 border border-input rounded-md disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                        </TableProvider>
                    </div>
                </>
            )}

            <ConfirmationDialog />

            {/* View Audience Contacts Modal */}
            {viewingAudienceContacts && (
                <Dialog
                    open={!!viewingAudienceContacts}
                    onClose={() => setViewingAudienceContacts(null)}
                    className="max-w-4xl"
                >
                    <DialogHeader>
                        <h2 className="text-xl font-semibold text-foreground">
                            Contacts in "{viewingAudienceContacts.name}"
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            {viewingAudienceContacts.type === 'static' 
                                ? 'Static list contacts' 
                                : 'Dynamic segment contacts'}
                        </p>
                    </DialogHeader>
                    <DialogBody>
                        {audienceContactsLoading ? (
                            <div className="text-center py-8 text-muted-foreground">
                                Loading contacts...
                            </div>
                        ) : audienceContacts.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                No contacts in this audience
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                {audienceContacts.map((contact, index) => (
                                    <div
                                        key={`${contact.contactType || 'client'}-${contact.contactId || index}`}
                                        className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border"
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-medium text-foreground">
                                                    {contact.name || 'Unnamed'}
                                                </span>
                                                {contact.contactType && (
                                                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                                        contact.contactType === 'client' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                                                        contact.contactType === 'lead' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                                                        'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                                                    }`}>
                                                        {contact.contactType === 'client' ? 'Client' :
                                                         contact.contactType === 'lead' ? 'Lead' : 'Account Contact'}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-sm text-muted-foreground">{contact.email}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </DialogBody>
                    <DialogFooter>
                        <button
                            onClick={() => setViewingAudienceContacts(null)}
                            className="px-4 py-2 border border-input rounded-md hover:bg-muted"
                        >
                            Close
                        </button>
                        <Link
                            to={`/audiences/${viewingAudienceContacts._id}/edit`}
                            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
                            onClick={() => setViewingAudienceContacts(null)}
                        >
                            Edit Audience
                        </Link>
                    </DialogFooter>
                </Dialog>
            )}
        </div>
    );
};

export default CampaignsEnhanced;


